<template>
  <div class="h3">Категории номеров</div>
  <div class="paragraph-s_h mb-10 mt-4 text-secondary">Выберите номера, которые будут продаваться по тарифу</div>

  <div v-if="!roomCategories?.length" class="d-flex flex-column align-center mt-10 py-10">
    <v-img :src="RoomCategoriesImage" width="160px" />
    <div class="caption text-center mt-10">У объекта размещения отсутствуют категории номеров</div>

    <e-btn variant="link" class="mt-6" prepend-icon-src="plus" @click="createRoomCategory"
      >Добавить категорию номера</e-btn
    >
  </div>

  <e-form v-else ref="formRef" data-wide-content @submit="next">
    <e-alert v-if="!selectedRoomCategories?.length && isDirty" variant="error" thin class="mb-4">
      Не выбрана ни одна категория номера
    </e-alert>

    <e-data-items
      v-model="selectedRoomCategories"
      item-value="cid"
      :content-wrap="false"
      :items="roomCategories"
      :headers="headers"
    >
      <template v-slot:[`header.data-table-select`]="props">
        <e-checkbox
          :model-value="props.allSelected"
          :square-selected="props.someSelected"
          :disabled="restartIntegrationRoomCategories?.length === roomCategories?.length"
          @update:modelValue="onSelectAllClick"
        />
      </template>
      <template v-slot:[`item.data-table-select`]="{ isSelected, internalItem, item, toggleSelect }">
        <e-tooltip
          v-if="
            restartIntegrationRoomCategories?.find((category) => category.roomCategoryCid === item.cid)?.quotaStatus ===
            QuotaStatus.QuotaInWork
          "
          :max-width="364"
        >
          <template #activator>
            <e-icon name="lock" class="text-secondary d-block" />
          </template>
          <div class="text-secondary">
            <div class="paragraph-s font-600 mb-2">Квота в работе</div>
            <div class="paragraph-xs">Исключение категорий номера из Вариантов размещения запрещается</div>
          </div>
        </e-tooltip>
        <e-checkbox
          v-else
          :model-value="isSelected(internalItem)"
          @update:modelValue="toggleSelect(internalItem, $event)"
        />
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <e-btn icon size="S" variant="ghost" class="ml-1 text-link" @click="editRoomCategory(item)">
          <e-icon name="documentText" />
        </e-btn>
      </template>
    </e-data-items>

    <e-btn variant="link" class="mt-6 mr-auto" prepend-icon-src="plus" @click="createRoomCategory">
      Добавить категорию номера
    </e-btn>
  </e-form>

  <room-category-dialog
    v-model="isRoomCategoryDialogOpened"
    :room-category="editingRoomCategory"
    @success:add="onAddRoomCategory"
    @success:update="onUpdateRoomCategory"
    @after-leave="onDialogClose"
  />
  <div class="d-flex mt-10">
    <e-btn v-if="!isEditMode" variant="secondary" prepend-icon-src="arrowLeft" @click="back">Назад</e-btn>
    <v-spacer />
    <e-btn
      variant="primary"
      append-icon-src="arrowRight"
      :loading="isLoading"
      :disabled="!roomCategories?.length"
      @click="submit"
    >
      Далее
    </e-btn>
  </div>
</template>

<script setup lang="ts">
import { ref, toRef } from 'vue';

import RoomCategoriesImage from '@/assets/img/pages/room-categories/no-room-categories.svg';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import { EAlert, EBtn, ECheckbox, EForm, EIcon, ETooltip } from '@/core/components/ui';
import { type DataTableHeaders } from '@/core/types/components/table';
import { QuotaStatus } from '@/graphql/generated';
import RoomCategoryDialog from '@/parts/room-categories/components/RoomCategoryDialog.vue';
import { type RoomCategoriesItemType } from '@/parts/room-categories/store/roomCategories';
import { useReverseIntegrationStore } from '@/parts/tariffs/store/reverse';

defineProps<{
  isEditMode: boolean;
}>();

const { activeStep, setActiveStep, integration, updateRoomCategories, restartIntegrationRoomCategories } =
  useReverseIntegrationStore();

const roomCategories = toRef(() => integration.value?.master?.steps.roomCategoriesSettings.roomCategories);

const selectedRoomCategories = ref<string[]>(
  integration.value?.master?.steps.roomCategoriesSettings.selectedRoomCategoryCIDs ?? [],
);

const formRef = ref<InstanceType<typeof EForm>>();

const submit = () => {
  formRef.value?.submit();
};

const back = () => {
  setActiveStep(activeStep.value - 1);
};

const isDirty = ref(false);
const isLoading = ref(false);

const next = async () => {
  isDirty.value = true;

  if (!selectedRoomCategories.value.length) {
    return;
  }

  isLoading.value = true;

  await updateRoomCategories(selectedRoomCategories.value);

  isLoading.value = false;

  setActiveStep(activeStep.value + 1);
};

const onSelectAllClick = (value: boolean) => {
  if (value) {
    selectedRoomCategories.value = roomCategories.value?.map((category) => category.cid) ?? [];
  } else {
    selectedRoomCategories.value =
      restartIntegrationRoomCategories.value?.map((category) => category.roomCategoryCid) ?? [];
  }
};

const isRoomCategoryDialogOpened = ref(false);
const editingRoomCategory = ref<RoomCategoriesItemType>();

const editRoomCategory = (item: RoomCategoriesItemType) => {
  editingRoomCategory.value = item;
  isRoomCategoryDialogOpened.value = true;
};

const createRoomCategory = () => {
  isRoomCategoryDialogOpened.value = true;
};

const onAddRoomCategory = (data: RoomCategoriesItemType) => {
  const roomMappingMaster = integration.value?.master?.steps.roomCategoriesSettings;

  if (!roomMappingMaster) {
    return;
  }

  roomMappingMaster.roomCategories.push(data);
  selectedRoomCategories.value.push(data.cid);
};

const onUpdateRoomCategory = (data: RoomCategoriesItemType) => {
  const roomMappingMaster = integration.value?.master?.steps.roomCategoriesSettings;

  if (!roomMappingMaster) {
    return;
  }

  const existingRoomCategoryIndex = roomMappingMaster.roomCategories.findIndex(
    (roomCategory) => roomCategory.cid === data.cid,
  );

  if (existingRoomCategoryIndex !== -1) {
    roomMappingMaster.roomCategories.splice(existingRoomCategoryIndex, 1, data);
  }
};

const onDialogClose = () => {
  editingRoomCategory.value = undefined;
};

const headers: DataTableHeaders = [
  { key: 'name[0].value', title: 'Категория номера' },
  { key: 'type.name', title: 'Тип', sortable: false, align: 'start', width: 160 },
  { key: 'seat.baseQuantity', title: 'Осн.', sortable: false, width: 80, align: 'start' },
  { key: 'seat.extQuantity', title: 'Доп.', sortable: false, width: 80, align: 'start' },
  { key: 'actions', sortable: false, width: 64, align: 'end' },
];
</script>

<style scoped lang="scss"></style>
